// Template for Global Commitment CSR Profile page 2021

import React from 'react';
import styled from '@emotion/styled/macro';
import siteVars from '../variables';
import vars from '@variables';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import ProfileContainer from '@components/global-commitment/gc-profile-container/gc-profile-container';
import HorizontalTable from '@components/horizontal-table/horizontal-table';
import TableRow from '@components/horizontal-table/table-row';
import createGcMetaData from '../utils/global-commitment/gc-metadata';
import SubHeader from '@components/global-commitment/sub-header/sub-header';
import SecondaryNav from '@components/secondary-nav/secondary-nav';
import FormattedText from '@components/global-commitment/formatted-text/formatted-text';
import MetricsFramework from '@components/key-metrics/metrics-framework';
import MetricsColumn from '@components/key-metrics/metrics-column';
import MetricsRow from '@components/key-metrics/metrics-row';
import VerticalBarGraph from '../design-system/src/components/vertical-bar-graph/vertical-bar-graph';
import { graphql } from 'gatsby';
import { transformTags } from '../utils/transform-data-functions.js';
import { SubHeaderAtom, PAtom } from '@atoms/text-atoms/text-atoms';
import { formatNumberWithCommas, convertStringToBoolean } from '@utils/utils';
import { AAtom, FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { getFooterData } from '../utils/translation-functions';
import {
  generateGcDropdownLinks,
  buildBarData,
} from '../utils/global-commitment/helper-functions';

export const query = graphql`
  query GcCsrPageQuery($id: String) {
    categoryLinks: allGcCsrDataCsv {
      nodes {
        organisation_name__b_1
        slug__b_4
      }
    }
    gcCsrPage: gcCsrDataCsv(id: { eq: $id }) {
      id
      category__b_0
      organisation_name__b_1
      slug__b_4
      meta_title__b_6_1
      meta_description__b_6_2
      previous_report_url__b_5
      first_year__rep
      _12_months_time_frame_for_quantitative_data__2_1
      _2018_plastic_recycled__metric_tonnes___17_2_4
      _2019_plastic_recycled__metric_tonnes___17_2_5
      _2019_plastic_recycled__metric_tonnes___alternative__18_4_3
      _2020_plastic_recycled__alternative___18_4_3
      _2020_plastic_recycled__metric_tonnes___18_1_1
      _2020_recycled__input_to_facility___metric_tonnes___18_1_2
      _2020_recycled__input_to_facility___metric_tonnes___alternative_text__18_1_2_alt
      activites_and_progress_made__19_1
      additional_details_on_investments__21_3
      afteruse_activities__16_1
      company_details_block__display__company_details_display
      data_verification_assurance_status__2_2
      description__1_1
      ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
      endnotes_block__display__endnotes_display
      headquarters_location__1_5
      interpreting_the_quantitative_data_submitted__18_9
      investments_block__display__investments_display
      joined_the_global_commitment__join_date
      key_metrics_block__display__key_metrics_display
      link_to_other_published_data__18_1
      organisation_name_sortable__b_1_bis
      planned_actions__19_2
      plastic_collected__alternative___18_4_1
      plastic_collected__metric_tonnes____18_1_4
      plastic_sorted__alternative___18_4_2
      plastic_sorted__metric_tonnes___18_1_3
      publish_report__b_3
      quantity_of_plastic_collection_sorted_and_recycled__display__quantity_plastic_display
      recycled_plastic_block__display__recycled_plastic_display
      regions_active__1_6
      reporting_details_block__display__reporting_details_display
      revenue_band__usd___1_4_2_1
      scope_of_reported_data__2_5
      scope_of_reporting__2_6
      source_of_plastic_recycled__as_a_percent_of_total_weight_of_plastic_recycled__output___18_6
      target__au_target_full
      target_plastic_recycled__alternative__au_target_excep
      target_plastic_recycled__metric_tonnes___au_target
      target_plastic_recycled__metric_tonnes___label__au_target_label
      thirdparty_verification_assurance__2_3
      total_invested_committed_over_the_reporting_period__21_1_1
      total_invested_committed_over_the_reporting_period__alternative_text__21_2
      visual_1__alt_text__18_4_3
      visual_1__display__v1_display
      webpage_or_report_addressing_progress__1_3
      website__1_2
    }
  }
`;

const Page = ({ data, className, pageContext }) => {
  // Primary nav and footer data handling
  const footerData = getFooterData('en-GB');
  const pageData = data.gcCsrPage;

  const companySecondaryNavData = [
    {
      isVisible: convertStringToBoolean(
        pageData.key_metrics_block__display__key_metrics_display
      ),
      title: 'key metrics',
      url: '#key-metrics',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.company_detais_block_display__company_details_display
      ),
      title: 'company details',
      url: '#company-details',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.reporting_details_block__display__reporting_details_display
      ),
      title: 'reporting details',
      url: '#reporting-details',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.quantity_of_plastic_collection_sorted_and_recycled__display__quantity_plastic_display
      ),
      title: 'portfolio',
      url: '#portfolio',
    },
    {
      isVisible: true,
      title: 'commitments',
      url: '#commitments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.investments_block__display__investments_display
      ),
      title: 'investments',
      url: '#investments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.endnotes_block__display__endnotes_display
      ),
      title: 'end notes',
      url: '#end-notes',
    },
  ].filter(element => element.isVisible === true);

  const linkData = data.categoryLinks.nodes;

  const organisationListingUrl = `${siteVars.GLOBAL_COMMITMENT_BASE_PATH}/${siteVars.GLOBAL_COMMITMENT_LISTING_PAGE_SLUG}`;

  const title = pageData.organisation_name__b_1;
  const category = pageData.category__b_0;
  const metaTitle = pageData.meta_title__b_6_1;
  const metaDescription = pageData.meta_description__b_6_2;

  // generate proper links for companies dropdown
  const gcDropdownLinks = generateGcDropdownLinks(linkData, category, title);

  // Tags data handling
  const transformedTags = transformTags(['Plastics']);

  const metaData = createGcMetaData(title, metaTitle, metaDescription);

  const gtmDataLayerVars = {
    pageId: pageContext.id,
    event: 'Page View',
    pageType: `standard page${
      pageContext.pageType ? ` - ${pageContext.pageType}` : null
    }`,
    topics: transformedTags,
  };

  return (
    <Layout
      footerData={footerData}
      translate={false}
      className={className}
      metaData={metaData}
      gtmDataLayerVars={gtmDataLayerVars}
      globals={pageContext?.globals ? pageContext.globals : null}
    >
      {' '}
      <div className="wrapper">
        <SectionLayout>
          <ProfileContainer>
            <SubHeader
              title={title}
              category={category}
              firstYearReport={pageData.first_year__rep}
              previousYearSlug={pageData.previous_report_url__b_5}
              organisationListingUrl={organisationListingUrl}
              linkData={gcDropdownLinks}
            />
            <div>
              <SecondaryNav tabs={companySecondaryNavData} />

              <div id="key-metrics" className="link-destination" />
              <MetricsFramework
                title="Key Metrics"
                footnote={pageData.not_aligned_caption__dev_note}
                isVisible={convertStringToBoolean(
                  pageData.key_metrics_block__display__key_metrics_display
                )}
              >
                <MetricsColumn>
                  {pageData.plastic_collected__metric_tonnes____18_1_4 ? (
                    <MetricsRow label="Plastic collected">
                      {`${formatNumberWithCommas(
                        pageData.plastic_collected__metric_tonnes____18_1_4
                      )} metric tonnes`}
                    </MetricsRow>
                  ) : (
                    <MetricsRow label="Plastic collected">
                      {pageData.plastic_collected__alternative___18_4_1}
                    </MetricsRow>
                  )}
                  {pageData.plastic_sorted__metric_tonnes___18_1_3 ? (
                    <MetricsRow label="Plastic sorted">
                      {`${formatNumberWithCommas(
                        pageData.plastic_sorted__metric_tonnes___18_1_3
                      )} metric tonnes`}
                    </MetricsRow>
                  ) : (
                    <MetricsRow label="Plastic sorted">
                      {pageData.plastic_sorted__alternative___18_4_2}
                    </MetricsRow>
                  )}
                </MetricsColumn>
                <MetricsColumn>
                  {convertStringToBoolean(
                    pageData.visual_1__display__v1_display
                  ) ? (
                    <VerticalBarGraph
                      type="numeric"
                      title="Plastic recycled (output - metric tonnes)"
                      bars={buildBarData([
                        {
                          date: '2018',
                          value:
                            pageData._2018_plastic_recycled__metric_tonnes___17_2_4,
                        },
                        {
                          date: '2019',
                          value:
                            pageData._2019_plastic_recycled__metric_tonnes___17_2_5,
                        },
                        {
                          date: '2020',
                          value:
                            pageData._2020_plastic_recycled__metric_tonnes___18_1_1,
                          colour: '#3780c1',
                        },
                      ])}
                      target={{
                        value:
                          pageData.target_plastic_recycled__metric_tonnes___au_target,
                        label: formatNumberWithCommas(
                          pageData.target_plastic_recycled__metric_tonnes___au_target
                        ),
                      }}
                    />
                  ) : (
                    <VerticalBarGraph
                      title="Plastic recycled (output - metric tonnes)"
                      altText={
                        pageData._2019_plastic_recycled__metric_tonnes___alternative__18_4_3
                      }
                    />
                  )}
                </MetricsColumn>
              </MetricsFramework>
            </div>

            <div id="company-details" className="link-destination" />
            <HorizontalTable
              title="Company Details"
              isVisible={convertStringToBoolean(
                pageData.company_detais_block_display__company_details_display
              )}
            >
              {pageData.description__1_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__1_1} />
                </TableRow>
              ) : null}
              {pageData.website__1_2 ? (
                <TableRow label="Website">
                  <PAtom>
                    <FlexibleLinkAtom url={pageData.website__1_2} />
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.revenue_band__usd___1_4_2_1 ? (
                <TableRow label="Revenue band (USD)">
                  <PAtom>{pageData.revenue_band__usd___1_4_2_1}</PAtom>
                </TableRow>
              ) : null}
              {pageData.headquarters_location__1_5 ? (
                <TableRow label="Headquarters location">
                  <PAtom>{pageData.headquarters_location__1_5}</PAtom>
                </TableRow>
              ) : null}
              {pageData.regions_active__1_6 ? (
                <TableRow label="Regions active">
                  <FormattedText inputText={pageData.regions_active__1_6} />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="reporting-details" className="link-destination" />
            <HorizontalTable
              title="Reporting details"
              isVisible={convertStringToBoolean(
                pageData.reporting_details_block__display__reporting_details_display
              )}
            >
              {pageData.data_verification_assurance_status__2_2 ? (
                <TableRow label="Data verification/assurance status">
                  <PAtom>
                    {pageData.data_verification_assurance_status__2_2}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.scope_of_reported_data__2_5 ? (
                <TableRow label="Scope of reported data">
                  <PAtom>{pageData.scope_of_reported_data__2_5}</PAtom>
                </TableRow>
              ) : null}
              {pageData._12_months_time_frame_for_quantitative_data__2_1 ? (
                <TableRow label="12-month quantitative data time frame">
                  <PAtom>
                    {pageData._12_months_time_frame_for_quantitative_data__2_1}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.joined_the_global_commitment__join_date ? (
                <TableRow label="Joined the Global Commitment">
                  <PAtom>
                    {pageData.joined_the_global_commitment__join_date}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7 ? (
                <TableRow label="Ellen MacArthur Foundation's Plastics Pact Network membership (as of 18/05/2021)">
                  <PAtom>
                    {
                      pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
                    }
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.webpage_or_report_addressing_progress__1_3 ? (
                <TableRow label="Webpage or report addressing progress">
                  <AAtom
                    href={pageData.webpage_or_report_addressing_progress__1_3}
                  >
                    {pageData.webpage_or_report_addressing_progress__1_3}
                  </AAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="portfolio" className="link-destination" />
            <HorizontalTable
              title="Quantity of plastic collected, sorted and recycled"
              isVisible={convertStringToBoolean(
                pageData.quantity_of_plastic_collection_sorted_and_recycled__display__quantity_plastic_display
              )}
            >
              <PAtom>
                See endnotes for more information on quantitative data
              </PAtom>
              <TableRow label="Collected for recycling">
                <PAtom>
                  {pageData.plastic_collected__metric_tonnes____18_1_4
                    ? `${formatNumberWithCommas(
                        pageData.plastic_collected__metric_tonnes____18_1_4
                      )} metric tonnes`
                    : pageData.plastic_collected__alternative___18_4_1}
                </PAtom>
              </TableRow>
              <TableRow label="Sorted for recycling">
                <PAtom>
                  {pageData.plastic_sorted__metric_tonnes___18_1_3
                    ? `${formatNumberWithCommas(
                        pageData.plastic_sorted__metric_tonnes___18_1_3
                      )} metric tonnes`
                    : pageData.plastic_sorted__alternative___18_4_2}
                </PAtom>
              </TableRow>
              <TableRow label="Recycled (input to facility)">
                <PAtom>
                  {pageData._2020_recycled__input_to_facility___metric_tonnes___18_1_2
                    ? `${formatNumberWithCommas(
                        pageData._2020_recycled__input_to_facility___metric_tonnes___18_1_2
                      )} metric tonnes`
                    : pageData._2020_recycled__input_to_facility___metric_tonnes___alternative_text__18_1_2_alt}
                </PAtom>
              </TableRow>
              <TableRow label="Recycled (output from facilities)">
                <PAtom>
                  {pageData._2020_plastic_recycled__metric_tonnes___18_1_1
                    ? `${formatNumberWithCommas(
                        pageData._2020_plastic_recycled__metric_tonnes___18_1_1
                      )} metric tonnes`
                    : pageData._2020_plastic_recycled__alternative___18_4_3}
                </PAtom>
              </TableRow>
              {pageData.source_of_plastic_recycled__as_a_percent_of_total_weight_of_plastic_recycled__output___18_6 ? (
                <TableRow label="Source of plastic recycled (as a % of total weight of plastic recycled - output)">
                  <FormattedText
                    inputText={
                      pageData.source_of_plastic_recycled__as_a_percent_of_total_weight_of_plastic_recycled__output___18_6
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.link_to_other_published_data__18_1 ? (
                <TableRow label="Link to other published data">
                  <AAtom href={pageData.link_to_other_published_data__18_1}>
                    {pageData.link_to_other_published_data__18_1}
                  </AAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <SubHeaderAtom displaySeparator={true}>Commitments</SubHeaderAtom>
            <div id="commitments" className="link-destination" />
            <HorizontalTable
              title="Increasing the quantity and quality of recycled/composted plastics"
              isVisible={convertStringToBoolean(
                pageData.recycled_plastic_block__display__recycled_plastic_display
              )}
            >
              <SubHeaderAtom>
                Progress made over the reporting period
              </SubHeaderAtom>
              <TableRow label="Recycled (output from facility)">
                <PAtom>
                  {pageData._2020_plastic_recycled__metric_tonnes___18_1_1
                    ? `${formatNumberWithCommas(
                        pageData._2020_plastic_recycled__metric_tonnes___18_1_1
                      )} metric tonnes`
                    : pageData._2020_plastic_recycled__alternative___18_4_3}
                </PAtom>
              </TableRow>
              {pageData.activites_and_progress_made__19_1 ? (
                <TableRow label="Activities and progress made">
                  <FormattedText
                    inputText={pageData.activites_and_progress_made__19_1}
                  />
                </TableRow>
              ) : null}
              <SubHeaderAtom displaySeparator={true}>
                Planned actions by 2025
              </SubHeaderAtom>
              {pageData.target__au_target_full ? (
                <TableRow label="Target">
                  <PAtom>{pageData.target__au_target_full}</PAtom>
                </TableRow>
              ) : null}
              {pageData.planned_actions__19_2 ? (
                <TableRow label="Planned actions">
                  <FormattedText inputText={pageData.planned_actions__19_2} />
                </TableRow>
              ) : null}
            </HorizontalTable>
            {/* TODO Is this needed it's in PDF */}
            {/* <p>15.4 {pageData.activites_and_progress_made__15_4}</p> */}

            <div id="investments" className="link-destination" />
            <HorizontalTable
              title="Investments"
              isVisible={convertStringToBoolean(
                pageData.investments_block__display__investments_display
              )}
            >
              <TableRow label="Total invested/committed over the reporting period towards achieving the commitments">
                <PAtom>
                  {pageData.total_invested_committed_over_the_reporting_period__21_1_1
                    ? `${formatNumberWithCommas(
                        pageData.total_invested_committed_over_the_reporting_period__21_1_1
                      )}`
                    : pageData.total_invested_committed_over_the_reporting_period__alternative_text__21_2}
                </PAtom>
              </TableRow>
              {pageData.additional_details_on_investments__21_3 ? (
                <TableRow label="Additional details on investments">
                  <FormattedText
                    inputText={pageData.additional_details_on_investments__21_3}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="end-notes" className="link-destination" />
            <HorizontalTable
              title="End Notes"
              isVisible={convertStringToBoolean(
                pageData.endnotes_block__display__endnotes_display
              )}
            >
              {pageData.thirdparty_verification_assurance__2_3 ? (
                <TableRow label="Third-party verification/assurance">
                  <FormattedText
                    inputText={pageData.thirdparty_verification_assurance__2_3}
                  />
                </TableRow>
              ) : null}
              {pageData.scope_of_reporting__2_6 ? (
                <TableRow label="Scope of reporting">
                  <FormattedText inputText={pageData.scope_of_reporting__2_6} />
                </TableRow>
              ) : null}
              {pageData.interpreting_the_quantitative_data_submitted__18_9 ? (
                <TableRow label="Interpreting the quantitative data submitted">
                  <FormattedText
                    inputText={
                      pageData.interpreting_the_quantitative_data_submitted__18_9
                    }
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
          </ProfileContainer>
        </SectionLayout>
      </div>
    </Layout>
  );
};

const StyledGcCsrPage = styled(Page)`
  .wrapper {
    margin: 12rem ${vars.spacingL};
  }
  .temporary-key-metrics {
    padding: 3rem 0 1rem 0;
    ${vars.fontSize8}
    color: ${vars.coreDarkGrey};
    border-bottom: 1px dashed ${vars.transparentCoreGrey};
    width: ${vars.contentMaxWidth};
  }
`;

export default StyledGcCsrPage;
